import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

import { Asset, InputAsset } from '@shared/models/asset.model'
import { ApiService } from '@shared/services/api.service'
import { PagesData } from '@shared/models/pages-data.model'
import { FilterValues } from '@shared/models/filter-values.model'
import { Vehicle } from '@shared/models/vehicle.model'
import { withFeedback } from '@core/interceptors/feedback-context'

@Injectable({ providedIn: 'root' })
export class AssetsService {
  private readonly basePath: string = '/assets'

  constructor(private readonly apiService: ApiService) {}

  getAssets(
    pageIndex = 1,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Asset | keyof Vehicle | string)[],
  ): Observable<PagesData<Asset>> {
    const inputFields = ['location'].concat(fields || []) as (keyof Asset)[]
    // TODO: switch when Go supports paginated list
    return this.apiService.getPageable<Asset>(this.basePath, pageIndex, pageSize, sort, filterValues, inputFields)
  }

  getAssetList(filterValues?: FilterValues, fields?: (keyof Asset | keyof Vehicle | string)[]): Observable<Asset[]> {
    return this.getAssets(1, 1000, null, filterValues, fields).pipe(map((data: PagesData<Asset>) => data.data))
  }

  getAsset(id: string | number, fields = ['lastTrackerHistorySensor', 'coordinates', 'todayData', 'gpsStatus']): Observable<Asset> {
    return this.apiService
      .get(`${this.basePath}/${id}`, {
        params: {
          ...(fields && { ['fields[]']: fields }),
        },
      })
      .pipe(map((response) => response['data']))
  }

  createAsset(inputAsset: InputAsset): Observable<Asset> {
    return this.apiService.post(this.basePath, inputAsset).pipe(map((response) => response['data']))
  }

  editAsset(assetId: number, inputAsset: InputAsset): Observable<Asset> {
    return this.apiService.patch(`${this.basePath}/${assetId}`, inputAsset).pipe(map((response) => response['data']))
  }

  deleteAsset(assetId: number) {
    return this.apiService.delete(`${this.basePath}/${assetId}`)
  }

  restoreAsset(id: number): Observable<Asset> {
    return this.apiService.patch(`${this.basePath}/${id}/restore`, {}).pipe(map((response) => response['data']))
  }

  pairWithSensor(assetId: number, sensorId: number): Observable<Asset> {
    return this.apiService
      .post(`${this.basePath}/${assetId}/sensors/${sensorId}/pair`, {}, { context: withFeedback() })
      .pipe(map((response) => response['data']))
  }

  unpairWithSensor(assetId: number, sensorId: number): Observable<Asset> {
    return this.apiService
      .post(`${this.basePath}/${assetId}/sensors/${sensorId}/unpair`, {}, { context: withFeedback() })
      .pipe(map((response) => response['data']))
  }
}
